import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'

const SERVICES_MENU = [
  {
    path: '/privat-rengoering',
    label: 'Privat rengøring',
    desc: 'Fleksibel rengøring af private hjem'
  },
  {
    path: '/erhvervsrengoering',
    label: 'Erhvervsrengøring',
    desc: 'Kontorer, erhverv og arbejdspladser'
  },
  {
    path: '/klinik-rengoering',
    label: 'Klinik rengøring',
    desc: 'Vores spidskompetence & erfaring',
    highlight: true
  },
  {
    path: '/hovedrengoering',
    label: 'Hovedrengøring',
    desc: 'Dybdegående og grundig gennemgang'
  },
  {
    path: '/flytterengoering',
    label: 'Flytterengøring',
    desc: 'Klargøring til ind- og fraflytning'
  }
]

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef(null)
  const closeTimeoutRef = useRef(null)

  // Stable scroll listener with hysteresis to avoid threshold bouncing/shaking
  useEffect(() => {
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY
          // Hysteresis gap: activate when scrolled past 40px, only deactivate when returned above 10px
          setIsScrolled(prev => {
            if (y > 40 && !prev) return true
            if (y < 10 && prev) return false
            return prev
          })
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false)
    setDropdownOpen(false)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
  }, [location.pathname])

  // Lock body scroll when mobile drawer is open so user scrolls only the menu
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Desktop dropdown hover handlers with grace timeout
  const handleDropdownEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setDropdownOpen(true)
  }

  const handleDropdownLeave = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false)
    }, 180)
  }

  const isServiceActive = SERVICES_MENU.some(s => location.pathname === s.path)

  return (
    <>
      {/* Top utility contact bar: sits in natural document flow so it scrolls off naturally without fighting sticky layout */}
      <div className="bg-[#092826] text-emerald-100/90 text-xs px-4 sm:px-6 lg:px-8 py-2 border-b border-[#0d3835]">
        <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4 sm:gap-6 text-emerald-100/80">
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-regular fa-location-dot text-[#f05a28]" aria-hidden="true" />
              <span>Sivvænget 6, 4622 Havdrup</span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5">
              <i className="fa-regular fa-clock text-[#f05a28]" aria-hidden="true" />
              <span>Mandag – Fredag efter aftale</span>
            </span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href="mailto:havdruprengoring@gmail.com"
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors focus-visible:outline-none focus-visible:text-amber-300"
              aria-label="Send e-mail til havdruprengoring@gmail.com"
            >
              <i className="fa-regular fa-envelope text-[#f05a28]" aria-hidden="true" />
              <span className="no-break">havdruprengoring@gmail.com</span>
            </a>
            <a
              href="tel:31341632"
              className="inline-flex items-center gap-1.5 font-semibold text-white hover:text-amber-300 transition-colors focus-visible:outline-none focus-visible:text-amber-300"
              aria-label="Ring på 31 34 16 32"
            >
              <i className="fa-regular fa-phone text-[#f05a28]" aria-hidden="true" />
              <span className="no-break">31 34 16 32</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar – cleanly sticky with smooth logo scaling */}
      <header
        className={`sticky top-0 z-40 w-full bg-white/95 backdrop-blur-md border-b transition-all duration-300 ease-in-out ${
          isScrolled ? 'border-slate-200/80 shadow-md' : 'border-slate-100 shadow-xs'
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center justify-between gap-4 transition-all duration-300 ease-in-out ${
              isScrolled ? 'h-16' : 'h-20'
            }`}
          >
            {/* Logo – smooth resizing based on scroll state */}
            <Logo compact={isScrolled} />

            {/* DESKTOP NAVIGATION: Visible ONLY at min-width 1200px as required */}
            <nav
              aria-label="Hovednavigation"
              className="hidden min-[1200px]:flex items-center gap-1 lg:gap-2"
            >
              {/* Forside */}
              <Link
                to="/"
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/'
                    ? 'bg-[#0d3835]/10 text-[#0d3835] font-semibold'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Forside
              </Link>

              {/* Ydelser Dropdown */}
              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  type="button"
                  onClick={() => setDropdownOpen(prev => !prev)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                    isServiceActive
                      ? 'bg-[#0d3835]/10 text-[#0d3835] font-semibold'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                >
                  <span>Ydelser</span>
                  <i
                    className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${
                      dropdownOpen ? 'rotate-180 text-[#f05a28]' : 'text-slate-400'
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {/* Dropdown Menu with safe hover bridge */}
                {dropdownOpen && (
                  <div className="absolute left-0 top-full pt-1.5 w-76 origin-top-left z-50 animate-in fade-in zoom-in-95 duration-150">
                    {/* Invisible hover bridge between trigger button and menu */}
                    <div className="absolute -top-3 left-0 right-0 h-3" />

                    <div className="rounded-xl border border-slate-100 bg-white p-2 shadow-xl ring-1 ring-slate-900/5 focus:outline-none">
                      <div className="space-y-1">
                        {SERVICES_MENU.map(service => {
                          const active = location.pathname === service.path
                          return (
                            <Link
                              key={service.path}
                              to={service.path}
                              onClick={() => setDropdownOpen(false)}
                              className={`flex flex-col rounded-lg px-3 py-2.5 transition-all cursor-pointer ${
                                active
                                  ? 'bg-[#fef0eb] text-[#0d3835] font-semibold border-l-3 border-[#f05a28] pl-3 ring-1 ring-orange-200 shadow-2xs'
                                  : 'hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-l-3 border-transparent'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${active ? 'font-bold text-[#0d3835]' : 'font-medium'}`}>
                                  {service.label}
                                </span>
                                <div className="flex items-center gap-1.5">
                                  {active && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#f05a28]">
                                      <span className="h-1.5 w-1.5 rounded-full bg-[#f05a28]" />
                                      Aktiv
                                    </span>
                                  )}
                                  {service.highlight && !active && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700 shadow-2xs">
                                      <i className="fa-solid fa-star text-[9px] text-amber-500" aria-hidden="true" />
                                      Spidskompetence
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className={`text-xs ${active ? 'text-[#0d3835]/80 font-normal' : 'text-slate-500 font-normal'}`}>
                                {service.desc}
                              </span>
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Om Havdrup Rengøring */}
              <Link
                to="/om-os"
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/om-os'
                    ? 'bg-[#0d3835]/10 text-[#0d3835] font-semibold'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Om Havdrup Rengøring
              </Link>

              {/* Kontakt */}
              <Link
                to="/kontakt"
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/kontakt'
                    ? 'bg-[#0d3835]/10 text-[#0d3835] font-semibold'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Kontakt
              </Link>
            </nav>

            {/* Desktop Quick CTA */}
            <div className="hidden min-[1200px]:flex items-center gap-3">
              <a
                href="tel:31341632"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3835] hover:text-[#f05a28] transition-colors py-2 px-3 rounded-lg hover:bg-slate-50"
              >
                <i className="fa-regular fa-phone text-[#f05a28]" aria-hidden="true" />
                <span className="no-break">31 34 16 32</span>
              </a>

              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-xl bg-[#f05a28] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#db4d1d] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f05a28] cursor-pointer"
              >
                <span>Få et tilbud</span>
                <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            {/* MOBILE & TABLET (<1200px): Phone button + Burger toggle */}
            <div className="flex min-[1200px]:hidden items-center gap-2">
              <a
                href="tel:31341632"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-lg bg-orange-50 text-[#f05a28] border border-orange-200 px-3 py-2 text-xs font-semibold hover:bg-orange-100 transition-colors cursor-pointer"
                aria-label="Ring på 31 34 16 32"
              >
                <i className="fa-regular fa-phone text-xs" aria-hidden="true" />
                <span className="no-break font-bold">Ring 31 34 16 32</span>
              </a>

              <button
                type="button"
                onClick={() => setMobileOpen(prev => !prev)}
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-[#0d3835] cursor-pointer"
                aria-expanded={mobileOpen}
                aria-label="Åbn mobilmenu"
              >
                {mobileOpen ? (
                  <i className="fa-regular fa-xmark text-lg" aria-hidden="true" />
                ) : (
                  <i className="fa-regular fa-bars text-lg" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* MOBILE DRAWER (<1200px) - INDEPENDENTLY SCROLLABLE SIDEBAR MENU */}
        {mobileOpen && (
          <div className="min-[1200px]:hidden border-t border-slate-200 bg-white shadow-2xl max-h-[calc(100dvh-4.25rem)] overflow-y-auto overscroll-contain overflow-x-hidden animate-in slide-in-from-top-2 duration-150">
            <div className="px-4 pt-3 pb-8 space-y-1">
              {/* Forside */}
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-between rounded-lg px-3.5 py-2.5 text-base font-medium transition-all ${
                  location.pathname === '/'
                    ? 'bg-sky-50 text-sky-800 font-bold border-l-3 border-sky-600 pl-3 ring-1 ring-sky-200/60 shadow-2xs'
                    : 'text-slate-800 hover:bg-slate-50 border-l-3 border-transparent'
                }`}
              >
                <span>Forside</span>
                {location.pathname === '/' && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-600" />
                    Aktiv
                  </span>
                )}
              </Link>

              {/* Mobile Services Accordion */}
              <div>
                <button
                  type="button"
                  onClick={() => setMobileServicesOpen(prev => !prev)}
                  className={`flex w-full items-center justify-between rounded-lg px-3.5 py-2.5 text-base font-medium transition-colors ${
                    isServiceActive ? 'text-sky-700 font-bold' : 'text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>Ydelser</span>
                    {isServiceActive && (
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                        Valgt
                      </span>
                    )}
                  </span>
                  <i
                    className={`fa-regular fa-chevron-down text-xs transition-transform ${
                      mobileServicesOpen ? 'rotate-180 text-sky-600' : 'text-slate-400'
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {mobileServicesOpen && (
                  <div className="ml-2 pl-2 border-l-2 border-orange-100 space-y-1 my-1">
                    {SERVICES_MENU.map(s => {
                      const active = location.pathname === s.path
                      return (
                        <Link
                          key={s.path}
                          to={s.path}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-all ${
                            active
                              ? 'bg-[#fef0eb] text-[#0d3835] font-bold border-l-3 border-[#f05a28] pl-3 ring-1 ring-orange-200 shadow-2xs'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-3 border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span>{s.label}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {active && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#f05a28]">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#f05a28]" />
                                Aktiv
                              </span>
                            )}
                            {s.highlight && !active && (
                              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-[#f05a28]">
                                Erfaring
                              </span>
                            )}
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Om Havdrup Rengøring */}
              <Link
                to="/om-os"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-between rounded-lg px-3.5 py-2.5 text-base font-medium transition-all ${
                  location.pathname === '/om-os'
                    ? 'bg-[#fef0eb] text-[#0d3835] font-bold border-l-3 border-[#f05a28] pl-3 ring-1 ring-orange-200 shadow-2xs'
                    : 'text-slate-800 hover:bg-slate-50 border-l-3 border-transparent'
                }`}
              >
                <span>Om Havdrup Rengøring</span>
                {location.pathname === '/om-os' && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#f05a28]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#f05a28]" />
                    Aktiv
                  </span>
                )}
              </Link>

              {/* Kontakt */}
              <Link
                to="/kontakt"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-between rounded-lg px-3.5 py-2.5 text-base font-medium transition-all ${
                  location.pathname === '/kontakt'
                    ? 'bg-[#fef0eb] text-[#0d3835] font-bold border-l-3 border-[#f05a28] pl-3 ring-1 ring-orange-200 shadow-2xs'
                    : 'text-slate-800 hover:bg-slate-50 border-l-3 border-transparent'
                }`}
              >
                <span>Kontakt</span>
                {location.pathname === '/kontakt' && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#f05a28]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#f05a28]" />
                    Aktiv
                  </span>
                )}
              </Link>

              {/* Quick Actions in Mobile Menu - Stacked buttons */}
              <div className="mt-5 pt-4 border-t border-slate-100 flex flex-col gap-2.5">
                <Link
                  to="/kontakt"
                  onClick={() => setMobileOpen(false)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#f05a28] px-4 py-3 text-sm font-semibold text-white shadow-xs hover:bg-[#db4d1d] transition-colors"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <a
                  href="tel:31341632"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                >
                  <i className="fa-regular fa-phone text-[#f05a28]" aria-hidden="true" />
                  <span className="no-break">Ring på 31 34 16 32</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Backdrop overlay for mobile drawer */}
      {mobileOpen && (
        <div
          className="min-[1200px]:hidden fixed inset-0 z-30 bg-slate-900/30 backdrop-blur-xs transition-opacity"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  )
}
